import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Camera, Upload, Save } from "lucide-react";
import { TranslatableInput } from "./TranslatableInput";
import { LocalizedContent } from "@/types";

interface PhotographerProfile {
  id: string;
  display_name: string;
  tagline: LocalizedContent;
  bio: LocalizedContent;
  location: LocalizedContent;
  phone: string;
  email: string;
  background_image_url: string;
  avatar_url: string;
  logo_url: string;
  currency: string;
}

export function ProfileManager() {
  const [profile, setProfile] = useState<PhotographerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState<{
    display_name: string;
    tagline: LocalizedContent;
    bio: LocalizedContent;
    location: LocalizedContent;
    phone: string;
    email: string;
    currency: string;
  }>({
    display_name: '',
    tagline: '',
    bio: '',
    location: '',
    phone: '',
    email: '',
    currency: 'USD'
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('photographer_profiles')
        .select('*')
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setProfile(data as any);
        setFormData({
          display_name: data.display_name || '',
          tagline: data.tagline || '',
          bio: data.bio || '',
          location: data.location || '',
          phone: data.phone || '',
          email: data.email || '',
          currency: data.currency || 'USD'
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast({
        title: "Error",
        description: "Failed to fetch profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (profile) {
        // Update existing profile
        const { error } = await supabase
          .from('photographer_profiles')
          .update(formData as any)
          .eq('id', profile.id);

        if (error) throw error;
        toast({ title: "Profile updated successfully!" });
      } else {
        // Create new profile
        const { data: user } = await supabase.auth.getUser();
        if (!user.user) throw new Error('Not authenticated');

        const { error } = await supabase
          .from('photographer_profiles')
          .insert([{ ...formData, user_id: user.user.id }] as any);

        if (error) throw error;
        toast({ title: "Profile created successfully!" });
      }

      fetchProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: "Error",
        description: "Failed to save profile",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'avatar' | 'background' | 'logo') => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(type);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${type}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('profile')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('profile')
        .getPublicUrl(fileName);

      // Update profile with new image URL
      if (profile) {
        const updateField = type === 'avatar' ? 'avatar_url' : type === 'logo' ? 'logo_url' : 'background_image_url';
        const { error } = await supabase
          .from('photographer_profiles')
          .update({ [updateField]: publicUrl })
          .eq('id', profile.id);

        if (error) throw error;
        toast({
          title: `${type === 'avatar' ? 'Avatar' : type === 'logo' ? 'Logo' : 'Background image'} updated successfully!`
        });
        fetchProfile();
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive",
      });
    } finally {
      setUploading(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8">Loading profile...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Profile Management</h2>
        <p className="text-muted-foreground">Update your photography business profile</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Images */}
        <Card>
          <CardHeader>
            <CardTitle>Profile Images</CardTitle>
            <CardDescription>Upload your avatar and background image</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Avatar */}
            <div className="space-y-2">
              <Label>Profile Avatar</Label>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-muted">
                  {profile?.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Camera className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  disabled={uploading === 'avatar'}
                  onClick={() => document.getElementById('avatar-upload')?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading === 'avatar' ? 'Uploading...' : 'Upload Avatar'}
                </Button>
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e, 'avatar')}
                />
              </div>
            </div>

            {/* Logo */}
            <div className="space-y-2">
              <Label>Business Logo</Label>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded overflow-hidden bg-muted">
                  {profile?.logo_url ? (
                    <img
                      src={profile.logo_url}
                      alt="Logo"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Camera className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  disabled={uploading === 'logo'}
                  onClick={() => document.getElementById('logo-upload')?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading === 'logo' ? 'Uploading...' : 'Upload Logo'}
                </Button>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e, 'logo')}
                />
              </div>
            </div>

            {/* Background Image */}
            <div className="space-y-2">
              <Label>Background Image</Label>
              <div className="space-y-2">
                <div className="w-full h-32 rounded overflow-hidden bg-muted">
                  {profile?.background_image_url ? (
                    <img
                      src={profile.background_image_url}
                      alt="Background"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Camera className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={uploading === 'background'}
                  onClick={() => document.getElementById('background-upload')?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading === 'background' ? 'Uploading...' : 'Upload Background'}
                </Button>
                <input
                  id="background-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e, 'background')}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Information */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Business Information</CardTitle>
            <CardDescription>Your photography business details</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="display_name">Business Name</Label>
                  <Input
                    id="display_name"
                    value={formData.display_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                    placeholder="Your Photography Business"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <TranslatableInput
                    label="Location"
                    value={formData.location}
                    onChange={(val) => setFormData(prev => ({ ...prev, location: val }))}
                    placeholder="City, State"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <TranslatableInput
                  label="Tagline"
                  value={formData.tagline}
                  onChange={(val) => setFormData(prev => ({ ...prev, tagline: val }))}
                  placeholder="Your photography tagline"
                />
              </div>

              <div className="space-y-2">
                <TranslatableInput
                  label="Bio"
                  component="textarea"
                  value={formData.bio}
                  onChange={(val) => setFormData(prev => ({ ...prev, bio: val }))}
                  placeholder="Tell your story and photography experience..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="your@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Input
                  id="currency"
                  value={formData.currency}
                  onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                  placeholder="USD"
                  maxLength={3}
                />
                <p className="text-sm text-muted-foreground">Enter 3-letter currency code (e.g., USD, EUR, GBP)</p>
              </div>

              <Button type="submit" disabled={saving} className="w-full md:w-auto">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Profile'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}